import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import CustomSelect from "../../components/common/CustomSelect";
import { roleIdProcurement } from "../../GlobalSetupComponents/Enum/RoleEnum";
import AsteriskStyle from "../../components/Form/Tooltip/AsteriskStyle";
import {
  formatDate,
  fromInputDate,
  toInputDate,
} from "../../utils/dateFormatter";
import UserService from "../../GlobalSetupComponents/Services/UserService";

const ReusableSittingForm = ({
  visible,
  onHide,
  onSubmit,
  formData,
  setFormData,
  onChange,
  isEditMode = false,
  editingSitting = null,
  // Customization props
  sidebarWidth = "600px",
  title = "Sitting",
  showAdvancedButtons = true,
  customButtonLogic = null, // Custom function to determine button visibility
}) => {
  const [sourceBy, setSourceBy] = useState([]);

  useEffect(() => {
    const fetchUserRoleBasedData = async () => {
      try {
        const body = { roleIds: roleIdProcurement };
        const response = await UserService.getUserRoleBasedNames(body);
        const data = response.data?.users || [];
        setSourceBy(data);
      } catch (error) {
        console.error("Error fetching users by role:", error);
      }
    };
    fetchUserRoleBasedData();
  }, []);

  // Default button logic
const getDefaultButtonLogic = () => {
  const isUpcomingOrNoUpdate =
    formData?.isCompleted === "Up Coming" ||
    formData?.isCompleted === "No Update";

  const isNotCompletedOrCancelled =
    formData.isCompleted !== "COMPLETED" &&
    formData.isCompleted !== "CANCELLED";

  const canShowAdvancedButtons =
    formData.stage !== "completed" &&
    formData.stage !== "cancelled";


  return {
   
    showPrimaryButton: isNotCompletedOrCancelled,
    showCompletedButton:
      isUpcomingOrNoUpdate && canShowAdvancedButtons && showAdvancedButtons,
    showCancelButton:
      isUpcomingOrNoUpdate && canShowAdvancedButtons && showAdvancedButtons,
  };
};


  // Use custom button logic if provided, otherwise use default
  const buttonLogic = customButtonLogic ? customButtonLogic(formData) : getDefaultButtonLogic();

  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onHide}
      style={{ width: sidebarWidth }}
      header={
        <div className="flex items-center justify-between w-full">
          <div className="font-semibold capitalize text-lg text-customGreen">
           {formData?.isCompleted ? `Update ${title} Details` : `Add New ${title}`}
            {formData.isCompleted && `- ${formData.isCompleted.toLowerCase()}`}
          </div>
        </div>
      }
    >
      <div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.();
          }}
          className="space-y-4"
        >
          {/* Sitting Title */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              {title} Title
              <AsteriskStyle />
            </label>
            <input
              type="text"
              name="sittingTitle"
              value={formData.sittingTitle || ""}
              onChange={onChange}
              placeholder="Type"
              className="col-span-2 px-3 py-2 input-focus"
              required
              disabled={buttonLogic.showPrimaryButton === false}
            />
          </div>

          {/* Sitting Spot */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              {title} Spot
              <AsteriskStyle />
            </label>
            <input
              type="text"
              name="sittingSpot"
              value={formData.sittingSpot || ""}
              onChange={onChange}
              placeholder="Type"
              className="col-span-2 px-3 py-2 input-focus"
              required
              disabled={buttonLogic.showPrimaryButton === false}
            />
          </div>

          {/* Primary Sitting Person */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              Primary {title} Person
              <AsteriskStyle />
            </label>
            <CustomSelect
              options={sourceBy}
              value={formData.primarySittingPerson || null}
              onChange={(selectedOption) =>
                onChange({
                  target: {
                    name: "primarySittingPerson",
                    value: selectedOption || null,
                  },
                })
              }
              placeholder="Select Company User with role"
              isClearable
              className="col-span-2"
              disabled={buttonLogic.showPrimaryButton === false}
            />
          </div>

          {/* Secondary Sitting Person */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              Secondary {title} Person
            </label>
            <CustomSelect
              options={sourceBy}
              value={formData.secondarySittingPerson || null}
              onChange={(selectedOption) =>
                onChange({
                  target: {
                    name: "secondarySittingPerson",
                    value: selectedOption || null,
                  },
                })
              }
              placeholder="Select Company User with role"
              isClearable
              className="col-span-2"
              disabled={buttonLogic.showPrimaryButton === false}
            />
          </div>

          {/* Meeting Person Type */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              Meeting Person Type
            </label>
            <select
              disabled={buttonLogic.showPrimaryButton === false}
              name="meetingPersonType"
              value={formData.meetingPersonType || ""}
              onChange={onChange}
              className="col-span-2 px-3 py-2 input-focus"
            >
              <option value="">Select Type</option>
              <option value="Vendor">Vendor</option>
              <option value="Mediator">Mediator</option>
              <option value="Trader">Trader</option>
            </select>
          </div>

          {/* Meeting Person Name */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              Meeting Person Name
            </label>
            <input
              type="text"
              name="meetingPersonName"
              value={formData.meetingPersonName || ""}
              onChange={onChange}
              placeholder="Enter Name"
              className="col-span-2 px-3 py-2 input-focus"
                disabled={buttonLogic.showPrimaryButton === false}
            />
          </div>

          {/* Sitting Date */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              {title} Date
              <AsteriskStyle />
            </label>
            <input
              type="date"
              name="sittingDate"
                disabled={buttonLogic.showPrimaryButton === false}
              value={
                formData.sittingDate ? toInputDate(formData.sittingDate) : ""
              }
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sittingDate: fromInputDate(e.target.value),
                })
              }
              className="col-span-2 px-3 py-2 input-focus"
              required
            />
          </div>

          {/* Sitting Time */}
          <div className="grid grid-cols-3 items-center gap-4">
            <label className="text-gray-700 font-medium">
              {title} Time
              <AsteriskStyle />
            </label>
            <input
              type="time"
              name="sittingTime"
                disabled={buttonLogic.showPrimaryButton === false}
              value={formData.sittingTime || ""}
              onChange={onChange}
              className="col-span-2 px-3 py-2 input-focus"
              required
            />
          </div>

          {/* Cancellation Reason - Show only when cancelled */}
          {formData.isCompleted === "CANCELLED" && (
            <div className="grid grid-cols-3 items-center gap-4">
              <label className="text-gray-700 font-medium">
                Cancellation Reason
              </label>
              <textarea
                rows={3}
                name="cancellationReason"
                disabled={buttonLogic.showPrimaryButton === false}
                value={formData.cancellationReason || ""}
                className="col-span-2 px-3 py-2 input-focus"
                readOnly
              />
            </div>
          )}

          {/* Completion Fields - Show only when completed */}
          {formData.isCompleted === "COMPLETED" && (
            <>
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-gray-700 font-medium">
                  Negotiated Area Type
                </label>
                <input
                  type="text"
                  name="negotiatedAreaType"
                  value={formData.negotiatedAreaType || ""}
                  onChange={onChange}
                  placeholder="Enter Area Type"
                  className="col-span-2 px-3 py-2 input-focus"
                    disabled={buttonLogic.showPrimaryButton === false}
                />
              </div>
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-gray-700 font-medium">
                  Negotiated Amount
                </label>
                <input
                  type="number"
                  name="negotiatedAmount"
                  value={formData.negotiatedAmount || ""}
                  onChange={onChange}
                  placeholder="Enter Amount"
                  className="col-span-2 px-3 py-2 input-focus"
                    disabled={buttonLogic.showPrimaryButton === false}
                />
              </div>
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-gray-700 font-medium">
                  Advance Amount
                </label>
                <input
                  type="number"
                  name="advanceAmount"
                  value={formData.advanceAmount || ""}
                  onChange={onChange}
                  placeholder="Enter Amount"
                    disabled={buttonLogic.showPrimaryButton === false}
                  className="col-span-2 px-3 py-2 input-focus"
                />
              </div>
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-gray-700 font-medium">Feedback</label>
                <textarea
                  rows={3}
                  name="feedback"
                  value={formData.feedback || ""}
                  onChange={onChange}
                  placeholder="Enter Feedback"
                    disabled={buttonLogic.showPrimaryButton === false}
                  className="col-span-2 px-3 py-2 input-focus"
                />
              </div>
            </>
          )}

          {/* Other Notes */}
          <div className="grid grid-cols-3 gap-4">
            <label className="text-gray-700 font-medium">Other Notes</label>
            <textarea
              name="otherNotes"
              value={formData.otherNotes || ""}
              onChange={onChange}
                disabled={buttonLogic.showPrimaryButton === false}
              placeholder="Notes"
              className="col-span-2 px-3 py-2 input-focus"
              rows="3"
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-4 flex items-center gap-3">
            {/* Primary Action Button (Add/Update) */}
            {buttonLogic.showPrimaryButton && (
              <button
                type="button"
                onClick={() => onSubmit("PENDING")}
                className="w-full mt-2 py-2 bg-customGreen text-white font-medium rounded-md transition-colors"
              >
                {formData?.isCompleted ? "Update" : "Add"}
              </button>
            )}

            {/* Completed Button */}
            {buttonLogic.showCompletedButton && (
              <button
                type="button"
                onClick={() => onSubmit("COMPLETED")}
                className="w-full mt-2 py-2 bg-green-200 text-green-700 font-medium rounded-md transition-colors"
              >
                Completed
              </button>
            )}

            {/* Cancel Button */}
            {buttonLogic.showCancelButton && (
              <button
                type="button"
                onClick={() => onSubmit("CANCELLED")}
                className="w-full mt-2 py-2 bg-red-200 text-red-700 font-medium rounded-md transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </Sidebar>
  );
};

export default ReusableSittingForm;